import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed, ref} from "vue";
import {errorToast, successToast} from "@/composables/useToasts";

const orderBy = ref('date');
const search = ref('');
const orderDirection = ref('desc');
const visibleRows = ref(50);

export const useAnnotationFilters = () => {
  const changeOrderBy = (name) => {
    if (orderBy.value === name) {
      orderDirection.value = orderDirection.value === 'asc' ? 'desc' : 'asc';
      return;
    }
    orderBy.value = name;
    orderDirection.value = 'desc';
  };

  return {
    search,
    orderBy,
    orderDirection,
    changeOrderBy,
  };
};

export const useAnnotations = () => {
  const query = useQuery({
    queryKey: ['seo-score-annotations'],
    queryFn: async () => {
      const {data} = await axios.get('/ajax/v2/admin/seo-score-annotations');

      return data.data;
    },
  });

  const annotations = computed(() =>
    [...query.data.value ?? []]
      .filter(annotation => {
        const searchLower = search.value.toLowerCase();
        return annotation.title.toLowerCase().includes(searchLower) ||
          annotation.description.toLowerCase().includes(searchLower);
      })
      .sort(function (a, b) {
        if (orderDirection.value === 'asc') {
          return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
        }

        return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
      })
      .slice(0, visibleRows.value)
  );

  return {
    query,
    annotations,
    visibleRows,
  };
};

export const useAnnotationMutations = () => {
  const queryClient = useQueryClient();

  const createAnnotationMutation = useMutation({
    mutationFn: async (data) => await axios.post('/ajax/v2/admin/seo-score-annotations', data),
    onSuccess: () => successToast('Annotation created successfully!'),
    onError: (error) => errorToast('Failed to create annotation.'),
    onSettled: () => queryClient.invalidateQueries({queryKey: ['seo-score-annotations']}),
  });

  const updateAnnotationMutation = useMutation({
    mutationFn: async ({id, data}) => await axios.patch(`/ajax/v2/admin/seo-score-annotations/${id}`, data),
    onSuccess: () => successToast('Annotation updated successfully!'),
    onError: (error) => errorToast('Failed to update annotation.'),
    onSettled: () => queryClient.invalidateQueries({queryKey: ['seo-score-annotations']}),
  });

  const deleteAnnotationMutation = useMutation({
    mutationFn: async (id) => await axios.delete(`/ajax/v2/admin/seo-score-annotations/${id}`),
    onSuccess: () => successToast('Annotation deleted successfully!'),
    onError: (error) => errorToast('Failed to delete annotation.'),
    onSettled: () => queryClient.invalidateQueries({queryKey: ['seo-score-annotations']}),
  });

  return {
    createAnnotationMutation,
    updateAnnotationMutation,
    deleteAnnotationMutation,
  };
};
